import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Handshake, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { Skeleton } from "@/components/ui/skeleton";
import { getCollaborationPosts } from '@/lib/supabase';
import { toast } from "@/hooks/use-toast";

// Define a type for our collaboration posts
interface CollaborationPost {
  id: string | number;
  user: {
    id: string;
    name: string;
    username: string;
    avatar: string;
  };
  content: string;
  tags: string[];
  created_at?: string;
}

const CollaborationCarousel = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentPosts, setCurrentPosts] = useState<CollaborationPost[]>([]);
  const navigate = useNavigate();

  // Fetch collaboration posts from the database
  useEffect(() => {
    const fetchCollaborationPosts = async () => {
      try {
        setIsLoading(true);
        const fetchedPosts = await getCollaborationPosts(10);
        
        if (fetchedPosts && fetchedPosts.length > 0) {
          // Transform the fetched posts to match our component's expected format
          const formattedPosts = fetchedPosts.map(post => ({
            id: post.id,
            user: {
              id: post.profiles?.id || post.user_id,
              name: post.profiles?.display_name || 'User',
              username: `@${post.profiles?.username || 'user'}`,
              avatar: post.profiles?.avatar_url || '/placeholder.svg',
            },
            content: post.content,
            tags: [], // Note: This would need to be fetched separately if we have post tags
            created_at: post.created_at
          }));
          
          setCurrentPosts(formattedPosts);
        } else {
          // If no posts found, show a message
          toast({
            title: "No collaboration posts",
            description: "Currently there are no posts open for collaboration.",
            variant: "default",
          });
        }
      } catch (error) {
        console.error('Error fetching collaboration posts:', error);
        toast({
          title: "Error loading collaboration posts",
          description: "Could not load collaboration posts from the server.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCollaborationPosts();
    
    // Auto-cycle through posts every 60 seconds if we have posts
    const interval = setInterval(() => {
      setCurrentPosts((prevPosts) => {
        if (prevPosts.length <= 1) return prevPosts;
        
        // Rotate the array by moving the first element to the end
        const newPosts = [...prevPosts];
        const firstPost = newPosts.shift();
        if (firstPost) newPosts.push(firstPost);
        return newPosts;
      });
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, []);

  // Handle navigation to the post page
  const handlePostClick = (postId: string | number, event: React.MouseEvent) => {
    // Check if the click was on a link (user profile link)
    if ((event.target as HTMLElement).closest('a')) {
      // Don't navigate to post if user clicked on a link
      return;
    }
    
    // Navigate to home page where posts are displayed
    navigate(`/home`, { 
      state: { highlightPostId: postId.toString() }
    });
    
    // Show a toast to indicate what's happening
    toast({
      title: "Viewing collaboration post",
      description: "Navigating to the post...",
      duration: 3000,
    });
  };

  if (isLoading) {
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Handshake className="h-5 w-5 text-purple-500" />
          <h3 className="text-lg font-medium">Open For Collaboration</h3>
        </div>
        <Card className="border border-vibebops-border">
          <CardContent className="p-4">
            <div className="flex gap-3 mb-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-3/4 mb-3" />
            <Skeleton className="h-6 w-40" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentPosts.length === 0) {
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Handshake className="h-5 w-5 text-purple-500" />
          <h3 className="text-lg font-medium">Open For Collaboration</h3>
        </div>
        <Card className="border border-vibebops-border bg-vibebops-card">
          <CardContent className="p-4 text-center py-8">
            <p className="text-sm text-vibebops-textMuted mb-3">No posts open for collaboration yet</p>
            <Link to="/home" className="text-xs text-vibebops-accent hover:underline">
              Create a post and mark it as "Open for Collaboration"
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Handshake className="h-5 w-5 text-purple-500" />
        <h3 className="text-lg font-medium">Open For Collaboration</h3>
      </div>
      
      <Carousel className="w-full">
        <CarouselContent>
          {currentPosts.map((post, index) => (
            <CarouselItem key={`collab-${post.id}`}>
              <Card 
                className="border border-vibebops-border bg-vibebops-card hover:border-purple-400 transition-colors cursor-pointer group"
                onClick={(e) => handlePostClick(post.id, e)}
              >
                <CardContent className="p-4">
                  <div className="flex gap-3 mb-3">
                    <Link 
                      to={`/profile/${post.user.id}`} 
                      className="h-10 w-10 rounded-full bg-gradient-to-r from-pink-500 to-violet-500 overflow-hidden flex-shrink-0 z-10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <img src={post.user.avatar} alt={post.user.name} className="h-full w-full object-cover" />
                    </Link>
                    <div>
                      <Link 
                        to={`/profile/${post.user.id}`} 
                        className="font-medium hover:underline z-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {post.user.name}
                      </Link>
                      <p className="text-xs text-vibebops-textMuted">{post.user.username}</p>
                    </div>
                  </div>
                  
                  <p className="text-sm mb-3 line-clamp-2">{post.content}</p>
                  
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {post.tags.map((tag, idx) => (
                        <Link 
                          key={idx} 
                          to={`/tag/${tag.replace('#', '')}`} 
                          className="text-xs text-vibebops-accent hover:underline z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {tag}
                        </Link>
                      ))}
                    </div>
                  )}
                  
                  <Badge 
                    variant="secondary" 
                    className="flex items-center gap-1 py-1 px-3 bg-purple-500 text-white border-purple-700 group-hover:bg-purple-600 transition-colors"
                  >
                    <Handshake className="h-3 w-3" />
                    <span>Open For Collaboration</span>
                  </Badge>
                </CardContent>
              </Card>
              <div className="mt-1 text-xs text-center text-vibebops-textMuted opacity-0 group-hover:opacity-100 transition-opacity">
                Click to view post
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="flex justify-center mt-2">
          <CarouselPrevious className="relative static translate-y-0 left-0 right-0 mx-2 h-7 w-7" />
          <CarouselNext className="relative static translate-y-0 left-0 right-0 mx-2 h-7 w-7" />
        </div>
      </Carousel>
    </div>
  );
};

export default CollaborationCarousel;
